import React, { useEffect } from 'react';
import { Modal, Form, Button } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faSpinner } from '@fortawesome/free-solid-svg-icons';

import { useUpdateUser } from '../../../services/users';
import { useForm } from '../../../hooks/useForm';
import TimezoneSelect from '../../common/TimezoneSelect';
import AddressInput from '../../common/AddressInput';
import dayjs from '../../../lib/day';

const DEFAULTS = {
  userFirstName: '',
  userLastName: '',
  userPreferredName: '',
  userEmail: '',
  userDateOfBirth: '',
  userPhone: '',
  userCity: '',
  userState: '',
  userTimezone: 'America/Chicago',
  addressLine1: '',
  addressLine2: '',
  addressCity: '',
  addressState: '',
  addressZip: '',
  addressCountry: 'US',
  latitude: null,
  longitude: null,
};

const UserModal = ({ show, toggle, user, onSuccess }) => {
  const { form, onChange, setValues } = useForm(DEFAULTS);

  useEffect(() => {
    if (user) {
      setValues({
        ...DEFAULTS,
        ...user,
        userPreferredName: user.userPreferredName || '',
        userDateOfBirth: user.userDateOfBirth
          ? dayjs.utc(user.userDateOfBirth).format('YYYY-MM-DD')
          : '',
      });
    } else {
      setValues(DEFAULTS);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const updateUser = useUpdateUser(
    () => {
      if (typeof onSuccess === 'function') {
        onSuccess();
      }
      toast.success('User successfully updated.');
      toggle();
    },
    { onError: (err) => toast.error(err.message) },
  );

  const onSubmit = (e) => {
    e.preventDefault();

    updateUser.mutate({
      userId: user.userId,
      payload: {
        userFirstName: form.userFirstName,
        userLastName: form.userLastName,
        userPreferredName: form.userPreferredName || null,
        userEmail: form.userEmail,
        userDateOfBirth: form.userDateOfBirth || null,
        userPhone: form.userPhone || null,
        // Mirror the structured city/state onto the legacy userCity /
        // userState columns so downstream consumers that still read them
        // (CSV export in usersController, dbqPeopleController, etc.) stay
        // in sync. The AddressInput display intentionally does NOT read
        // these legacy fields anymore — they're write-only mirrors.
        userCity: form.addressCity || form.userCity || null,
        userState: form.addressState || form.userState || null,
        userTimezone: form.userTimezone,
        addressLine1: form.addressLine1 || null,
        addressLine2: form.addressLine2 || null,
        addressCity: form.addressCity || null,
        addressState: form.addressState || null,
        addressZip: form.addressZip || null,
        addressCountry: form.addressCountry || 'US',
        latitude: form.latitude ?? null,
        longitude: form.longitude ?? null,
      },
    });
  };

  if (!show || !user) return null;

  // Address rule: empty or verified; never partial. Lets admins save edits
  // for legacy clients with no address. Partial addresses (some strings, or
  // strings without lat/lng) are blocked to keep data clean.
  const addressLine1Trim = (form.addressLine1 || '').trim();
  const addressCityTrim = (form.addressCity || '').trim();
  const addressStateTrim = (form.addressState || '').trim();
  const addressZipTrim = (form.addressZip || '').trim();
  const addressEmpty =
    !addressLine1Trim &&
    !addressCityTrim &&
    !addressStateTrim &&
    !addressZipTrim &&
    form.latitude == null &&
    form.longitude == null;
  const addressVerified =
    !!addressLine1Trim &&
    !!addressCityTrim &&
    !!addressStateTrim &&
    !!addressZipTrim &&
    form.latitude != null &&
    form.longitude != null;
  const addressOk = addressEmpty || addressVerified;

  return (
    <Modal show={show} onHide={() => toggle()}>
      <Modal.Body>
        <h3>Edit User</h3>
        <form id="edit-user-form" onSubmit={onSubmit}>
          <Form.Group className="mb-3">
            <Form.Label>First Name</Form.Label>
            <Form.Control
              name="userFirstName"
              onChange={onChange}
              value={form.userFirstName}
              required
              placeholder="First Name"
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Last Name</Form.Label>
            <Form.Control
              name="userLastName"
              onChange={onChange}
              value={form.userLastName}
              required
              placeholder="Last Name"
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Preferred Name</Form.Label>
            <Form.Control
              name="userPreferredName"
              onChange={onChange}
              value={form.userPreferredName}
              placeholder="Nickname / Goes by"
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Email</Form.Label>
            <Form.Control
              type="email"
              name="userEmail"
              onChange={onChange}
              value={form.userEmail}
              required
              placeholder="user@email.com"
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Date of Birth</Form.Label>
            <Form.Control
              type="date"
              name="userDateOfBirth"
              onChange={onChange}
              value={form.userDateOfBirth}
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Phone</Form.Label>
            <Form.Control
              name="userPhone"
              onChange={onChange}
              value={form.userPhone || ''}
              placeholder="Phone Number"
            />
          </Form.Group>
          <AddressInput
            value={{
              line1: form.addressLine1 || '',
              line2: form.addressLine2 || '',
              city: form.addressCity || '',
              state: form.addressState || '',
              zip: form.addressZip || '',
              country: form.addressCountry || 'US',
              latitude: form.latitude ?? null,
              longitude: form.longitude ?? null,
            }}
            onChange={(next) =>
              setValues({
                ...form,
                addressLine1: next.line1 || '',
                addressLine2: next.line2 || '',
                addressCity: next.city || '',
                addressState: next.state || '',
                addressZip: next.zip || '',
                addressCountry: next.country || 'US',
                latitude: next.latitude ?? null,
                longitude: next.longitude ?? null,
              })
            }
            requireVerified
            label="Address"
            placeholder="Start typing the user's street address..."
            idPrefix="user-address"
          />
          <Form.Group className="mb-3">
            <Form.Label>Timezone</Form.Label>
            <TimezoneSelect
              name="userTimezone"
              onChange={onChange}
              value={form.userTimezone}
            />
          </Form.Group>
        </form>
      </Modal.Body>
      <Modal.Footer>
        <Button
          type="submit"
          variant="success"
          form="edit-user-form"
          disabled={updateUser.isLoading || !addressOk}
          title={
            !addressOk
              ? 'Finish the address (pick a verified suggestion) or clear it entirely to enable Save.'
              : undefined
          }
        >
          <FontAwesomeIcon
            icon={updateUser.isLoading ? faSpinner : faCheck}
            spin={updateUser.isLoading}
          />{' '}
          Save
        </Button>{' '}
        <Button variant="link" onClick={() => toggle()}>
          Cancel
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default UserModal;
